import React from 'react'
import {gql} from '@apollo/client'
import PropTypes from 'prop-types'
import useActiveLenders from '../../hooks/useActiveLenders'
// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
import withCacheClient from '@cred/shared/hoc/withCacheClient'

const GET_LENDERS_FOR_VERTICAL = gql`
  query GET_LENDERS_FOR_VERTICAL($vertical: String!, $loanPurpose: String) {
    getLendersForVertical(vertical: $vertical, loanPurpose: $loanPurpose) {
      slug
    }
  }
  `,

  /**
   * Component used to dynamically pull the lender count from verticals to be displayed in marketing text.
   * @param {String} vertical - specific vertical to query lenders from
   * @param {String} loanPurpose - you can also call this query with a second variable
   * named 'loanPurpose' if you were to want to filter results further (e.g., some personal loan lenders do not do home improvement loans)
   * @param {String} startText - clause before the count is displayed (e.g., if we wanted to display
   * 'Rates from up to <count> lenders, then the startText would be 'Rates from up to')
   * @param {String} endText - clause after the count is displayed
   * @param {String} defaultText - fallback text to be returned if the query fails
   * @param {Function} transformCount - function to be called with returned count value to modify its appearance (e.g., turn the integer into a word). NOTE: THE VALUE PASSED TO THE FUNCTION WILL BE A NUMBER TYPE
   * @returns {String} - Currently a Text component interpolated with the query result and props. Could eventually
   * pass the component to be returned as a prop.
   */
  LendersText = props => {
    const provideFullResults = true,
      results = useActiveLenders(props.vertical, props.loanPurpose, provideFullResults)

    return composeLendersStringFromPropsAndQuery(results, props)
  },

  // We're omitting loading state since this will be server-side rendered
  composeLendersStringFromPropsAndQuery = (queryResult, props) => {
    const {error, data} = queryResult,
      {startText, endText, defaultText, transformCount} = props,
      formattedStartText = startText.trim(),
      formattedEndText = endText.trim()

    if (error) {
      return defaultText
    }

    if (data && data.getLendersForVertical) {
      const lenderCount = data.getLendersForVertical.length

      return `${formattedStartText} ${transformCount(lenderCount)} ${formattedEndText}`
    }

    return ''
  }

LendersText.propTypes = {
  vertical: PropTypes.oneOf(['refi', 'in_school', 'personal_loans']).isRequired,
  loanPurpose: PropTypes.string,
  startText: PropTypes.string,
  endText: PropTypes.string,
  defaultText: PropTypes.string,
  transformCount: PropTypes.func
}

LendersText.defaultProps = {
  startText: 'Rates from up to',
  endText: 'lenders',
  defaultText: 'Rates from our many lenders',
  loanPurpose: null,
  transformCount: count => count
}

export {GET_LENDERS_FOR_VERTICAL, LendersText}
export default /*#__PURE__*/ withCacheClient(LendersText)
